/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  VodOttActionDetailsService,
  VodSocuLastViewedPositionService
} from "@tivocorp/data-domain-lib";
import type {
  VodOttActionDetailsList,
  TVodOttActionDetailsByContentIdParams,
  TVodOttActionDetailsByOfferIdParams,
  TVodOttActionDetailsByUnifiedCollectionIdParams,
  TVodSocuLastViewedPositionGetParams,
  TVodSocuLastViewedPositionSetParams
} from "@tivocorp/data-domain-lib";
import type { LastViewedPositionList } from "client-code/last-viewed-position";

import type { IVodInteractor } from "../../interfaces";
import { Log } from "../../logging/Log";
import { ApplicationFeatureArea } from "../../enums";
import { TIVO_SERVICES } from "../../../config";
import { BaseInteractor } from "../base";
import type { TNetworkOptions } from "../../types/TNetworkOptions";

/**
 * Feature interactor for VOD domain operations.
 *
 * Extends BaseInteractor so service-param construction (accessToken via
 * TokenManager, bodyId, TSRI endpoint resolution) is handled uniformly.
 * Service instances are NOT stored as class members — a fresh instance is
 * created on every method call so the interactor holds no mutable state.
 */
export class VodInteractor extends BaseInteractor implements IVodInteractor {
  private static readonly TAG: string = "VodInteractor";

  private async createVodOttActionDetailsService(): Promise<VodOttActionDetailsService> {
    const params = await this.buildServicesParams(
      TIVO_SERVICES.VodOttActionDetails,
      ApplicationFeatureArea.VOD
    );
    return new VodOttActionDetailsService(params);
  }

  private async createVodSocuLastViewedPositionService(): Promise<VodSocuLastViewedPositionService> {
    const params = await this.buildServicesParams(
      TIVO_SERVICES.VodSocuLastViewedPositionService,
      ApplicationFeatureArea.VOD
    );
    return new VodSocuLastViewedPositionService(params);
  }

  public async fetchOttContentActionDetails(
    params: TVodOttActionDetailsByContentIdParams,
    isForced: boolean = true,
    options?: TNetworkOptions
  ): Promise<VodOttActionDetailsList> {
    return this.executeGuarded(
      "fetchOttContentActionDetails",
      async (guardedSignal: AbortSignal): Promise<VodOttActionDetailsList> => {
        Log.d(VodInteractor.TAG, "fetchOttContentActionDetails() called");
        const service = await this.createVodOttActionDetailsService();
        return service.fetchContentActionDetailsV2(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  public async fetchOttOfferActionDetails(
    params: TVodOttActionDetailsByOfferIdParams,
    isForced: boolean = true,
    options?: TNetworkOptions
  ): Promise<VodOttActionDetailsList> {
    return this.executeGuarded(
      "fetchOttOfferActionDetails",
      async (guardedSignal: AbortSignal): Promise<VodOttActionDetailsList> => {
        Log.d(VodInteractor.TAG, "fetchOttOfferActionDetails() called");
        const service = await this.createVodOttActionDetailsService();
        return service.fetchOfferActionDetailsV2(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  public async getUnifiedCollectionActionDetails(
    params: TVodOttActionDetailsByUnifiedCollectionIdParams,
    isForced: boolean = true,
    options?: TNetworkOptions
  ): Promise<VodOttActionDetailsList> {
    return this.executeGuarded(
      "getUnifiedCollectionActionDetails",
      async (guardedSignal: AbortSignal): Promise<VodOttActionDetailsList> => {
        Log.d(VodInteractor.TAG, "getUnifiedCollectionActionDetails() called");
        const service = await this.createVodOttActionDetailsService();
        return service.fetchUnifiedCollectionActionDetailsV2(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  public async setSocuLastViewedPosition(
    params: TVodSocuLastViewedPositionSetParams,
    options?: TNetworkOptions
  ): Promise<void> {
    return this.executeGuarded(
      "setSocuLastViewedPosition",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(VodInteractor.TAG, "setSocuLastViewedPosition() called");
        const service = await this.createVodSocuLastViewedPositionService();
        return service.setSocuLastViewedPosition(params, guardedSignal);
      },
      options
    );
  }

  public async setVodLastViewedPosition(
    params: TVodSocuLastViewedPositionSetParams,
    options?: TNetworkOptions
  ): Promise<void> {
    return this.executeGuarded(
      "setVodLastViewedPosition",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(VodInteractor.TAG, "setVodLastViewedPosition() called");
        const service = await this.createVodSocuLastViewedPositionService();
        return service.setVodLastViewedPosition(params, guardedSignal);
      },
      options
    );
  }

  public async fetchSocuLastViewedPosition(
    params: TVodSocuLastViewedPositionGetParams,
    options?: TNetworkOptions
  ): Promise<LastViewedPositionList> {
    return this.executeGuarded(
      "fetchSocuLastViewedPosition",
      async (guardedSignal: AbortSignal): Promise<LastViewedPositionList> => {
        Log.d(VodInteractor.TAG, "fetchSocuLastViewedPosition() called");
        const service = await this.createVodSocuLastViewedPositionService();
        return service.fetchSocuLastViewedPosition(params, guardedSignal);
      },
      options
    );
  }
}
